import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardHeader, Input, Modal, useSnackbar } from '@hue/design-system';
import { KeyRound, LockKeyhole, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authenticatorApi, getApiError } from '../lib/api.js';
import { AuthenticatorHelp } from './authenticator-help.js';
import { MfaSetup } from './mfa-qr.js';
import { PageHeader } from './page-title.js';

const codeSchema = z.string().regex(/^\d{6}$/u, 'Informe os 6 dígitos.');
const proofSchema = z.object({
  password: z.string().min(1, 'Informe sua senha.'),
  code: codeSchema,
});
const confirmSchema = z.object({ code: codeSchema });

/// Segurança da própria pessoa em Configurações (ADR 0081). Como a Aparência, não tem permissão: toda
/// pessoa com acesso a alcança, porque a conta é dela.
export function CompanySecurityScreen() {
  const [open, setOpen] = useState(false);
  return (
    <section aria-labelledby="security-title">
      <PageHeader
        trail="Configurações / Segurança"
        id="security-title"
        title="Segurança"
        description="Como você confirma que é você ao entrar no sistema."
      />
      <Card className="grid gap-4 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardHeader title="Autenticador" icon={<Smartphone className="size-4" />} />
            <p className="mt-1 text-sm text-content-muted">
              O aplicativo que gera o código de 6 números pedido ao entrar. Troque quando mudar de
              celular ou quiser passar do celular para o navegador, ou o contrário.
            </p>
          </div>
          <AuthenticatorHelp />
        </div>
        <div>
          <Button type="button" variant="outline" onClick={() => setOpen(true)}>
            Trocar autenticador
          </Button>
        </div>
      </Card>
      {open ? <ChangeAuthenticatorModal onClose={() => setOpen(false)} /> : null}
    </section>
  );
}

function ChangeAuthenticatorModal({ onClose }: { onClose: () => void }) {
  const snackbar = useSnackbar();
  const [change, setChange] = useState<{ changeToken: string; mfaUri: string }>();
  const [requestError, setRequestError] = useState<string>();
  const proof = useForm({
    resolver: zodResolver(proofSchema),
    defaultValues: { password: '', code: '' },
  });
  const confirmation = useForm({
    resolver: zodResolver(confirmSchema),
    defaultValues: { code: '' },
  });

  async function begin(data: z.infer<typeof proofSchema>) {
    setRequestError(undefined);
    try {
      setChange(await authenticatorApi.beginChange(data));
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  async function confirm(data: z.infer<typeof confirmSchema>) {
    if (!change) return;
    setRequestError(undefined);
    try {
      await authenticatorApi.confirmChange({ changeToken: change.changeToken, code: data.code });
      snackbar.success('Autenticador trocado. O cadastro antigo deixou de valer.');
      onClose();
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  const alert = requestError ? (
    <div
      role="alert"
      className="rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
    >
      {requestError}
    </div>
  ) : null;

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Trocar autenticador"
      description={
        change
          ? 'Cadastre este QR Code no aparelho novo. Até você confirmar, o autenticador atual continua valendo.'
          : 'Confirme sua senha e o código que o autenticador atual mostra agora.'
      }
    >
      {change ? (
        <form
          key="confirm"
          noValidate
          className="space-y-4"
          onSubmit={(event) => void confirmation.handleSubmit(confirm)(event)}
        >
          <MfaSetup uri={change.mfaUri} qrClassName="mx-auto w-44" />
          {alert}
          <Input
            label="Código do autenticador novo"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            leftIcon={<KeyRound className="size-4" />}
            error={confirmation.formState.errors.code?.message}
            {...confirmation.register('code')}
          />
          <Button className="w-full" loading={confirmation.formState.isSubmitting}>
            Confirmar troca
          </Button>
        </form>
      ) : (
        <form
          key="proof"
          noValidate
          className="space-y-4"
          onSubmit={(event) => void proof.handleSubmit(begin)(event)}
        >
          {alert}
          <Input
            label="Sua senha"
            type="password"
            autoComplete="current-password"
            leftIcon={<LockKeyhole className="size-4" />}
            error={proof.formState.errors.password?.message}
            {...proof.register('password')}
          />
          <Input
            label="Código do autenticador atual"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            leftIcon={<KeyRound className="size-4" />}
            error={proof.formState.errors.code?.message}
            {...proof.register('code')}
          />
          <Button className="w-full" loading={proof.formState.isSubmitting}>
            Continuar
          </Button>
        </form>
      )}
    </Modal>
  );
}
